import { ProviderGoalSnapshot, ProviderGoalAction } from "./providerGoal.ts";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import * as SchemaIssue from "effect/SchemaIssue";
import * as SchemaTransformation from "effect/SchemaTransformation";
import * as Struct from "effect/Struct";
import { ProviderOptionSelections } from "./model.ts";
import { RepositoryIdentity } from "./environment.ts";
import {
  ApprovalRequestId,
  CheckpointRef,
  CommandId,
  EventId,
  IsoDateTime,
  MessageId,
  NonNegativeInt,
  PositiveInt,
  ProjectId,
  ProviderItemId,
  ThreadId,
  TrimmedNonEmptyString,
  TurnId,
} from "./baseSchemas.ts";
import { ProviderInstanceId } from "./providerInstance.ts";
import { SnapShotSource } from "./snapShot.ts";
import { ProviderUserInputAnswers } from "./providerUserInput.ts";
import { DesignSystemMention, DesignSystemMentionList } from "./designSystemMention.ts";
import { ThreadWorktreeLocation } from "./worktrees.ts";
import {
  ThreadPullRequestCollectionFields,
  ThreadPullRequestLink,
  ThreadPullRequestObservation,
  ThreadPullRequestStack,
} from "./threadPullRequests.ts";

export const ORCHESTRATION_WS_METHODS = {
  dispatchCommand: "orchestration.dispatchCommand",
  getTurnDiff: "orchestration.getTurnDiff",
  getFullThreadDiff: "orchestration.getFullThreadDiff",
  replayEvents: "orchestration.replayEvents",
  getArchivedShellSnapshot: "orchestration.getArchivedShellSnapshot",
  synchronizeHostViewOptimized: "orchestration.synchronizeHostViewOptimized",
  synchronizeHostView: "orchestration.synchronizeHostView",
  getThreadHistoryPage: "orchestration.getThreadHistoryPage",
  subscribeThread: "orchestration.subscribeThread",
} as const;

export const ProviderApprovalPolicy = Schema.Literals([
  "untrusted",
  "on-failure",
  "on-request",
  "never",
]);
export type ProviderApprovalPolicy = typeof ProviderApprovalPolicy.Type;
export const ProviderSandboxMode = Schema.Literals([
  "read-only",
  "workspace-write",
  "danger-full-access",
]);
export type ProviderSandboxMode = typeof ProviderSandboxMode.Type;

/**
 * The repository-relative directory an old thread ran in when its retired Thread
 * Scope was a Feature Workspace. Derived once from the stored creation event so
 * the thread can say its new turns run at the project root instead.
 */
const LegacyThreadWorkingDirectory = Schema.optional(Schema.NullOr(TrimmedNonEmptyString));

/**
 * The working directory a thread created under a retired Feature Workspace
 * Thread Scope ran in, read from its stored creation event; null for every
 * other thread. Projected once so the server and clients agree on it.
 */
export function legacyWorkingDirectoryOfRetiredScope(scope: unknown): string | null {
  if (typeof scope !== "object" || scope === null) return null;
  const candidate = scope as { readonly type?: unknown; readonly workingDirectory?: unknown };
  if (candidate.type !== "feature-workspace" || typeof candidate.workingDirectory !== "string") {
    return null;
  }
  const workingDirectory = candidate.workingDirectory.trim();
  return workingDirectory.length > 0 && workingDirectory !== "." ? workingDirectory : null;
}

/**
 * The Action a user message was sent with: a step of research work picked in
 * the composer for that one message, such as Ingest or Design · Update. The
 * prompt and Skill Bundle invocation it arranged travel beside it; this is what
 * the user picked, so the transcript and the Next Action can read it back.
 */
export const ThreadActionSelection = Schema.Struct({
  id: TrimmedNonEmptyString,
  variant: Schema.optionalKey(TrimmedNonEmptyString),
});
export type ThreadActionSelection = typeof ThreadActionSelection.Type;

/**
 * `ModelSelection` — selection of a model on a configured provider instance.
 *
 * The routing key is `instanceId` (a user-defined slug identifying one
 * configured provider instance). Drivers, credentials, working-directory
 * bindings, and any other per-instance state are recovered from the
 * runtime registry via the instance id.
 *
 * Wire legacy: persisted selections produced before the driver/instance
 * split carried a `provider: <driver-id>` field instead. The schema absorbs
 * that shape via a pre-decoding transform — `{provider, model}` is promoted
 * to `{instanceId: defaultInstanceIdForDriver(provider), model}`. No
 * post-decode compatibility code lives in the runtime; the transform is the
 * only compat surface.
 */
const ModelSelectionWire = Schema.Struct({
  instanceId: ProviderInstanceId,
  model: TrimmedNonEmptyString,
  options: Schema.optionalKey(ProviderOptionSelections),
});

// Source shape for persisted legacy payloads. Fields are typed as
// `Schema.Unknown` so malformed drafts still make it into the transform and
// fail validation through the target schema (with proper error messages)
// rather than at the source-struct layer where the error is less actionable.
const ModelSelectionSource = Schema.Struct({
  provider: Schema.optional(Schema.Unknown),
  instanceId: Schema.optional(Schema.Unknown),
  model: Schema.Unknown,
  options: Schema.optional(Schema.Unknown),
});

export const ModelSelection = ModelSelectionSource.pipe(
  Schema.decodeTo(
    ModelSelectionWire,
    SchemaTransformation.transformOrFail({
      decode: (raw) => {
        // Resolve the routing key: prefer an explicit `instanceId`; fall
        // back to promoting the legacy `provider` slug (the canonical
        // `defaultInstanceIdForDriver` mapping) so persisted rollout-era
        // payloads decode without data loss. The target schema brands the
        // string as `ProviderInstanceId`.
        const instanceIdSource =
          raw.instanceId !== undefined
            ? raw.instanceId
            : typeof raw.provider === "string"
              ? raw.provider
              : undefined;
        const base: Record<string, unknown> = {
          instanceId: instanceIdSource,
          model: raw.model,
        };
        if (raw.options !== undefined) base.options = raw.options;
        return Effect.succeed(base as typeof ModelSelectionWire.Encoded);
      },
      encode: (value) => {
        const base: Record<string, unknown> = {
          model: value.model,
          instanceId: value.instanceId,
        };
        if (value.options !== undefined) base.options = value.options;
        return Effect.succeed(base as typeof ModelSelectionSource.Encoded);
      },
    }),
  ),
);
export type ModelSelection = typeof ModelSelection.Type;

export const RuntimeMode = Schema.Literals([
  "approval-required",
  "auto-accept-edits",
  "auto",
  "full-access",
]);
export type RuntimeMode = typeof RuntimeMode.Type;
export const DEFAULT_RUNTIME_MODE: RuntimeMode = "full-access";
export const ProviderInteractionMode = Schema.Literals(["default", "plan"]);
export type ProviderInteractionMode = typeof ProviderInteractionMode.Type;
export const DEFAULT_PROVIDER_INTERACTION_MODE: ProviderInteractionMode = "default";
export const ProviderRequestKind = Schema.Literals([
  "command",
  "file-read",
  "file-change",
  "mcp-elicitation",
]);
export type ProviderRequestKind = typeof ProviderRequestKind.Type;
export const AssistantDeliveryMode = Schema.Literals(["buffered", "paragraph", "streaming"]);
export type AssistantDeliveryMode = typeof AssistantDeliveryMode.Type;
export const ProviderApprovalDecision = Schema.Literals([
  "accept",
  "acceptForSession",
  "acceptAlways",
  "decline",
  "cancel",
]);
export type ProviderApprovalDecision = typeof ProviderApprovalDecision.Type;
/**
 * One approval choice an Agent Provider offers for a request. Providers that
 * advertise their own choices (an MCP elicitation naming the connector it wants
 * to reach) send these so the client renders what the provider actually
 * accepts; the client falls back to the standard accept/decline pair when a
 * request carries none.
 */
export const ProviderApprovalOption = Schema.Struct({
  decision: ProviderApprovalDecision,
  label: TrimmedNonEmptyString,
  /** Provider-supplied caution shown next to the option, such as a prompt injection warning. */
  warning: Schema.optional(TrimmedNonEmptyString),
});
export type ProviderApprovalOption = typeof ProviderApprovalOption.Type;
export const PROVIDER_SEND_TURN_MAX_INPUT_CHARS = 120_000;
/** Ceiling on attachments carried by one turn. */
export const PROVIDER_SEND_TURN_MAX_ATTACHMENTS = 8;
export const PROVIDER_SEND_TURN_SUPPORTED_IMAGE_MIME_TYPES = [
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;
const PROVIDER_SEND_TURN_SUPPORTED_IMAGE_MIME_TYPE_SET = new Set<string>(
  PROVIDER_SEND_TURN_SUPPORTED_IMAGE_MIME_TYPES,
);
/** Whether a turn can carry this image as-is, rather than needing a conversion first. */
export function isProviderSendTurnSupportedImageMimeType(mimeType: string): boolean {
  return PROVIDER_SEND_TURN_SUPPORTED_IMAGE_MIME_TYPE_SET.has(mimeType.toLowerCase());
}

/** Ceiling for one image attachment on a turn. Clients compress to fit it. */
export const PROVIDER_SEND_TURN_MAX_IMAGE_BYTES = 10 * 1024 * 1024;
/** Ceiling for one non-image file attachment on a turn. */
export const PROVIDER_SEND_TURN_MAX_FILE_BYTES = 50 * 1024 * 1024;
/**
 * Largest file, or folder entry, a client sends inline on the turn as a data
 * URL. A larger one is uploaded first through the signed attachment upload, so
 * no single file puts tens of megabytes of base64 on the WebSocket.
 */
export const PROVIDER_SEND_TURN_MAX_INLINE_FILE_BYTES = 4 * 1024 * 1024;
const CHAT_ATTACHMENT_ID_MAX_CHARS = 128;
const ATTACHMENT_HOST_PATH_MAX_CHARS = 4096;
// Correlation id is command id by design in this model.
export const CorrelationId = CommandId;
export type CorrelationId = typeof CorrelationId.Type;

const ChatAttachmentId = TrimmedNonEmptyString.check(
  Schema.isMaxLength(CHAT_ATTACHMENT_ID_MAX_CHARS),
  Schema.isPattern(/^[a-z0-9_-]+$/i),
);
export type ChatAttachmentId = typeof ChatAttachmentId.Type;

export const ChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  id: ChatAttachmentId,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt,
  /** Present when the image came from a SnapShot of a captured window. */
  source: Schema.optional(SnapShotSource),
});
export type ChatImageAttachment = typeof ChatImageAttachment.Type;

const UploadChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt,
  dataUrl: TrimmedNonEmptyString,
  source: Schema.optional(SnapShotSource),
});
export type UploadChatImageAttachment = typeof UploadChatImageAttachment.Type;

const AttachmentDisplayName = TrimmedNonEmptyString.check(Schema.isMaxLength(255));
const AttachmentRelativePath = TrimmedNonEmptyString.check(
  Schema.makeFilter(
    (relativePath) => {
      const normalized = relativePath.replaceAll("\\", "/");
      const unsafe =
        normalized.startsWith("/") ||
        /^[A-Za-z]:\//.test(normalized) ||
        normalized.split("/").includes("..");
      return !unsafe || "Attachment paths must stay inside their snapshot";
    },
    { identifier: "AttachmentRelativePath" },
  ),
);

/**
 * Where a persisted attachment's bytes live.
 *
 * Present means the attachment is a live reference to a path the user picked
 * on the Environment host, and nothing was copied. Absent means the bytes were
 * uploaded without a host path (a pasted image, a browser `File`) and were
 * stored under the Host Store's attachments directory instead. Records written
 * before live references existed have no host path, so absent also reads
 * correctly for every attachment persisted by an older Environment Service.
 */
const AttachmentHostPath = Schema.optional(
  TrimmedNonEmptyString.check(Schema.isMaxLength(ATTACHMENT_HOST_PATH_MAX_CHARS)),
);

export const PastedTextAttachmentSource = Schema.TaggedStruct("pasted-text", {});
export type PastedTextAttachmentSource = typeof PastedTextAttachmentSource.Type;

export const ChatFileAttachment = Schema.Struct({
  type: Schema.Literal("file"),
  id: ChatAttachmentId,
  name: AttachmentDisplayName,
  mimeType: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(255))),
  sizeBytes: NonNegativeInt,
  hostPath: AttachmentHostPath,
  /** Clipboard text folded into a file by a client, rather than a picked file. */
  source: Schema.optional(PastedTextAttachmentSource),
});
export type ChatFileAttachment = typeof ChatFileAttachment.Type;

export const ChatFolderAttachmentEntry = Schema.Struct({
  relativePath: AttachmentRelativePath,
  sizeBytes: NonNegativeInt,
});
export type ChatFolderAttachmentEntry = typeof ChatFolderAttachmentEntry.Type;

export const ChatFolderAttachment = Schema.Struct({
  type: Schema.Literal("folder"),
  id: ChatAttachmentId,
  name: AttachmentDisplayName,
  sizeBytes: NonNegativeInt,
  entries: Schema.Array(ChatFolderAttachmentEntry),
  hostPath: AttachmentHostPath,
});
export type ChatFolderAttachment = typeof ChatFolderAttachment.Type;

export const UploadChatFileBytesAttachment = Schema.Struct({
  type: Schema.Literal("file"),
  name: AttachmentDisplayName,
  mimeType: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(255))),
  sizeBytes: NonNegativeInt,
  dataUrl: TrimmedNonEmptyString,
});
export type UploadChatFileBytesAttachment = typeof UploadChatFileBytesAttachment.Type;

export const UploadChatFileHostPathAttachment = Schema.Struct({
  type: Schema.Literal("file"),
  name: AttachmentDisplayName,
  mimeType: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(255))),
  sizeBytes: NonNegativeInt,
  hostPath: TrimmedNonEmptyString,
});
export type UploadChatFileHostPathAttachment = typeof UploadChatFileHostPathAttachment.Type;

/**
 * A file whose bytes already reached the Environment through a signed
 * attachment upload (`attachments.createUploadUrl`), so the turn carries only
 * the pending upload's id. Clients use it for files too large to ride the
 * turn inline; the Environment claims the upload for the thread on send.
 */
export const UploadChatFileUploadedAttachment = Schema.Struct({
  type: Schema.Literal("file"),
  name: AttachmentDisplayName,
  mimeType: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(255))),
  sizeBytes: NonNegativeInt,
  uploadedAttachmentId: ChatAttachmentId,
});
export type UploadChatFileUploadedAttachment = typeof UploadChatFileUploadedAttachment.Type;

export const UploadChatFileAttachment = Schema.Union([
  UploadChatFileHostPathAttachment,
  UploadChatFileUploadedAttachment,
  UploadChatFileBytesAttachment,
]);
export type UploadChatFileAttachment = typeof UploadChatFileAttachment.Type;

const UploadChatFolderBytesEntry = Schema.Struct({
  relativePath: AttachmentRelativePath,
  sizeBytes: NonNegativeInt,
  dataUrl: TrimmedNonEmptyString,
});

/** A folder entry too large to inline, sent ahead through the signed attachment upload. */
const UploadChatFolderUploadedEntry = Schema.Struct({
  relativePath: AttachmentRelativePath,
  sizeBytes: NonNegativeInt,
  uploadedAttachmentId: ChatAttachmentId,
});

export const UploadChatFolderAttachmentEntry = Schema.Union([
  UploadChatFolderUploadedEntry,
  UploadChatFolderBytesEntry,
]);
export type UploadChatFolderAttachmentEntry = typeof UploadChatFolderAttachmentEntry.Type;

export const UploadChatFolderEntrySummary = Schema.Struct({
  relativePath: AttachmentRelativePath,
  sizeBytes: NonNegativeInt,
});
export type UploadChatFolderEntrySummary = typeof UploadChatFolderEntrySummary.Type;

export const UploadChatFolderBytesAttachment = Schema.Struct({
  type: Schema.Literal("folder"),
  name: AttachmentDisplayName,
  sizeBytes: NonNegativeInt,
  entries: Schema.Array(UploadChatFolderAttachmentEntry),
});
export type UploadChatFolderBytesAttachment = typeof UploadChatFolderBytesAttachment.Type;

export const UploadChatFolderHostPathAttachment = Schema.Struct({
  type: Schema.Literal("folder"),
  name: AttachmentDisplayName,
  sizeBytes: NonNegativeInt,
  hostPath: TrimmedNonEmptyString,
  entries: Schema.Array(UploadChatFolderEntrySummary),
});
export type UploadChatFolderHostPathAttachment = typeof UploadChatFolderHostPathAttachment.Type;

export const UploadChatFolderAttachment = Schema.Union([
  UploadChatFolderHostPathAttachment,
  UploadChatFolderBytesAttachment,
]);
export type UploadChatFolderAttachment = typeof UploadChatFolderAttachment.Type;

export const ChatAttachment = Schema.Union([
  ChatImageAttachment,
  ChatFileAttachment,
  ChatFolderAttachment,
]);
export type ChatAttachment = typeof ChatAttachment.Type;
export const UploadChatAttachment = Schema.Union([
  UploadChatImageAttachment,
  UploadChatFileAttachment,
  UploadChatFolderAttachment,
]);
export type UploadChatAttachment = typeof UploadChatAttachment.Type;

export const ProjectScriptIcon = Schema.Literals([
  "play",
  "test",
  "lint",
  "configure",
  "build",
  "debug",
]);
export type ProjectScriptIcon = typeof ProjectScriptIcon.Type;

export const ProjectScript = Schema.Struct({
  id: TrimmedNonEmptyString,
  name: TrimmedNonEmptyString,
  command: TrimmedNonEmptyString,
  icon: ProjectScriptIcon,
  runOnWorktreeCreate: Schema.Boolean,
  /**
   * URL to open in the in-app browser preview when this script runs (or
   * when the user explicitly requests a preview). Optional; only honored on
   * the desktop build.
   */
  previewUrl: Schema.optional(TrimmedNonEmptyString),
  /**
   * When true, automatically open the preview panel pointed at `previewUrl`
   * the moment this script starts. Ignored without `previewUrl` or on web.
   */
  autoOpenPreview: Schema.optional(Schema.Boolean),
});
export type ProjectScript = typeof ProjectScript.Type;

/** How an AFAR Project came to be, when it matters to it afterwards. */
export const ProjectCreationOrigin = Schema.Literal("references");
export type ProjectCreationOrigin = typeof ProjectCreationOrigin.Type;

/**
 * A Project Study that started or was skipped, recorded once and never undone.
 * Absent while a project created from references still awaits it.
 */
export const ProjectStudyRecord = Schema.Union([
  Schema.Struct({ status: Schema.Literal("started"), threadId: ThreadId, at: IsoDateTime }),
  Schema.Struct({ status: Schema.Literal("skipped"), at: IsoDateTime }),
]);
export type ProjectStudyRecord = typeof ProjectStudyRecord.Type;

export const OrchestrationProject = Schema.Struct({
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.NullOr(ModelSelection),
  scripts: Schema.Array(ProjectScript),
  createdFrom: Schema.optional(ProjectCreationOrigin),
  study: Schema.optional(ProjectStudyRecord),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  deletedAt: Schema.NullOr(IsoDateTime),
});
export type OrchestrationProject = typeof OrchestrationProject.Type;

export const OrchestrationMessageRole = Schema.Literals(["user", "assistant", "system"]);
export type OrchestrationMessageRole = typeof OrchestrationMessageRole.Type;

/** Exact provider-native skill identity chosen from a provider catalog row. */
export const ProviderSkillInvocation = Schema.Struct({
  name: TrimmedNonEmptyString,
  path: TrimmedNonEmptyString,
  /** Provider-instance catalog that issued this exact path. */
  providerInstanceId: Schema.optional(ProviderInstanceId),
  /** Project cwd used for discovery; refreshed after a validated project switch. */
  catalogCwd: Schema.optional(TrimmedNonEmptyString),
  /** Exact selected-token range in the submitted prompt. Older clients may omit it. */
  promptStart: Schema.optional(NonNegativeInt),
  promptEnd: Schema.optional(NonNegativeInt),
});
export type ProviderSkillInvocation = typeof ProviderSkillInvocation.Type;
export const ProviderSkillInvocationList = Schema.Array(ProviderSkillInvocation);

/**
 * Every Design System the host still addresses by its four flat fields. Only
 * Delete uses them now: it is the one Design Operation the host applies, so it
 * is the one with a revision worth checking. The agent-backed members carry a
 * `DesignSystemMention` instead, whose path already encodes the storage slug.
 */
const DesignSystemTargetFields = {
  designSystemSlug: TrimmedNonEmptyString,
  slug: TrimmedNonEmptyString,
  displayName: TrimmedNonEmptyString,
  expectedRevision: TrimmedNonEmptyString,
} as const;

/**
 * An agent-backed Design Operation names its target by carrying the Design
 * System Mention itself. Nothing resolves a mention to a slug over the wire:
 * `designSystemSlugFromMentionPath` in `@afar-research/afar` reads it off the
 * path, so no agent-backed operation depends on the Design read model.
 *
 * `expectedRevision` is absent by design — every operation that had something
 * to check a revision against is either host-applied (Delete) or gone.
 */
const DesignMentionTargetFields = {
  target: DesignSystemMention,
  brief: Schema.optional(TrimmedNonEmptyString),
} as const;

export const DesignOperationSubmission = Schema.Union([
  Schema.Struct({
    action: Schema.Literal("create-design"),
    displayName: TrimmedNonEmptyString,
    brief: Schema.optional(TrimmedNonEmptyString),
  }),
  Schema.Struct({
    action: Schema.Literal("update-design"),
    ...DesignMentionTargetFields,
  }),
  // The artifact's name is absent on purpose: it is the first thing the Skill
  // Bundle establishes by grilling, and a name typed before any discussion is
  // the worst version of it. A queued entry renders as target plus brief.
  Schema.Struct({
    action: Schema.Literal("add-design-template"),
    ...DesignMentionTargetFields,
  }),
  Schema.Struct({
    action: Schema.Literal("add-design-example"),
    ...DesignMentionTargetFields,
  }),
  Schema.Struct({
    action: Schema.Literal("create-design-sample"),
    ...DesignMentionTargetFields,
  }),
  // Rename collects a brief like its siblings, not a name. What the user types
  // is what they want said about the rename — "it should say what it is for" is
  // a sentence, and taking it literally made it the Design System's new name —
  // so the skill establishes the name with the user and derives the slug from
  // it. `nextDisplayName` decodes renames sent before that, and nothing writes
  // it: dropping it outright would make those threads unreadable.
  Schema.Struct({
    action: Schema.Literal("rename-design-system"),
    ...DesignMentionTargetFields,
    nextDisplayName: Schema.optional(TrimmedNonEmptyString),
  }),
  Schema.Struct({
    action: Schema.Literal("delete-design-system"),
    ...DesignSystemTargetFields,
    confirmationName: TrimmedNonEmptyString,
    confirmationSlug: TrimmedNonEmptyString,
  }),
]);
export type DesignOperationSubmission = typeof DesignOperationSubmission.Type;

/**
 * The Workspace Action a send *is*, when it is one. A Thread Queue Entry
 * carries it while the send waits, and the user message carries it for good —
 * the operation is what the user sent, not an artifact of having waited.
 */
export const MessageOperationSubmission = Schema.Union([DesignOperationSubmission]);
export type MessageOperationSubmission = typeof MessageOperationSubmission.Type;

export const OrchestrationMessage = Schema.Struct({
  id: MessageId,
  role: OrchestrationMessageRole,
  text: Schema.String,
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  designSystemMentions: DesignSystemMentionList,
  /**
   * The Workspace Action this message submitted, when it submitted one. The
   * transcript draws the operation rather than the prompt the App arranged from
   * it, and it must be able to do that whether or not the send ever waited in
   * the Thread Queue — so the identity lives here, not only on an entry.
   */
  operation: Schema.optional(MessageOperationSubmission),
  /** The Action this message was sent with, when the user picked one. */
  action: Schema.optional(ThreadActionSelection),
  turnId: Schema.NullOr(TurnId),
  streaming: Schema.Boolean,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  sequence: Schema.optional(NonNegativeInt),
});
export type OrchestrationMessage = typeof OrchestrationMessage.Type;

export const OrchestrationProposedPlanId = TrimmedNonEmptyString;
export type OrchestrationProposedPlanId = typeof OrchestrationProposedPlanId.Type;

export const OrchestrationProposedPlan = Schema.Struct({
  id: OrchestrationProposedPlanId,
  turnId: Schema.NullOr(TurnId),
  planMarkdown: TrimmedNonEmptyString,
  implementedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  implementationThreadId: Schema.NullOr(ThreadId).pipe(
    Schema.withDecodingDefault(Effect.succeed(null)),
  ),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationProposedPlan = typeof OrchestrationProposedPlan.Type;

const SourceProposedPlanReference = Schema.Struct({
  threadId: ThreadId,
  planId: OrchestrationProposedPlanId,
});
export type SourceProposedPlanReference = typeof SourceProposedPlanReference.Type;

export const OrchestrationSessionStatus = Schema.Literals([
  "idle",
  "starting",
  "running",
  "ready",
  "interrupted",
  "stopped",
  "error",
]);
export type OrchestrationSessionStatus = typeof OrchestrationSessionStatus.Type;

export const OrchestrationSession = Schema.Struct({
  threadId: ThreadId,
  status: OrchestrationSessionStatus,
  providerName: Schema.NullOr(TrimmedNonEmptyString),
  providerInstanceId: Schema.optional(ProviderInstanceId),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  activeTurnId: Schema.NullOr(TurnId),
  lastError: Schema.NullOr(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});
export type OrchestrationSession = typeof OrchestrationSession.Type;

export const OrchestrationCheckpointFile = Schema.Struct({
  path: TrimmedNonEmptyString,
  kind: TrimmedNonEmptyString,
  additions: NonNegativeInt,
  deletions: NonNegativeInt,
});
export type OrchestrationCheckpointFile = typeof OrchestrationCheckpointFile.Type;

export const OrchestrationCheckpointStatus = Schema.Literals(["ready", "missing", "error"]);
export type OrchestrationCheckpointStatus = typeof OrchestrationCheckpointStatus.Type;

export const TurnOrigin = Schema.Literals(["dispatched", "provider-initiated"]);
export type TurnOrigin = typeof TurnOrigin.Type;

export const OrchestrationCheckpointSummary = Schema.Struct({
  origin: Schema.optional(TurnOrigin),
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});
export type OrchestrationCheckpointSummary = typeof OrchestrationCheckpointSummary.Type;

export const OrchestrationThreadActivityTone = Schema.Literals([
  "info",
  "tool",
  "approval",
  "error",
]);
export type OrchestrationThreadActivityTone = typeof OrchestrationThreadActivityTone.Type;

export const OrchestrationThreadActivity = Schema.Struct({
  id: EventId,
  tone: OrchestrationThreadActivityTone,
  kind: TrimmedNonEmptyString,
  summary: TrimmedNonEmptyString,
  payload: Schema.Unknown,
  turnId: Schema.NullOr(TurnId),
  sequence: Schema.optional(NonNegativeInt),
  createdAt: IsoDateTime,
});
export type OrchestrationThreadActivity = typeof OrchestrationThreadActivity.Type;

const OrchestrationLatestTurnState = Schema.Literals([
  "running",
  "interrupted",
  "completed",
  "error",
]);
export type OrchestrationLatestTurnState = typeof OrchestrationLatestTurnState.Type;

export const OrchestrationLatestTurn = Schema.Struct({
  origin: TurnOrigin,
  turnId: TurnId,
  state: OrchestrationLatestTurnState,
  requestedAt: IsoDateTime,
  startedAt: Schema.NullOr(IsoDateTime),
  completedAt: Schema.NullOr(IsoDateTime),
  assistantMessageId: Schema.NullOr(MessageId),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
});
export type OrchestrationLatestTurn = typeof OrchestrationLatestTurn.Type;

/** Immutable managed-worktree intent captured when a bootstrapped thread is created. */
export const ThreadWorktreeBootstrapIdentity = Schema.Struct({
  projectCwd: TrimmedNonEmptyString,
  baseBranch: TrimmedNonEmptyString,
  branch: Schema.optional(TrimmedNonEmptyString),
  startFromOrigin: Schema.Boolean,
  location: ThreadWorktreeLocation,
});
export type ThreadWorktreeBootstrapIdentity = typeof ThreadWorktreeBootstrapIdentity.Type;

export const ThreadManagement = Schema.Struct({
  settledAt: Schema.NullOr(IsoDateTime),
  snoozedAt: Schema.NullOr(IsoDateTime),
  snoozedUntil: Schema.NullOr(IsoDateTime),
});
export type ThreadManagement = typeof ThreadManagement.Type;

/**
 * A thread's placement in the sidebar's user-arranged order. `pinnedAt`
 * lifts the thread out of the Active list into the pinned block; the two
 * order keys are fractional index keys, so a drag writes ONE key to ONE
 * thread and never touches its neighbours. Absent keys sort below the
 * arranged run in creation order, which is where every thread starts.
 */
export const ThreadPlacement = Schema.Struct({
  pinnedAt: Schema.NullOr(IsoDateTime),
  pinOrderKey: Schema.NullOr(TrimmedNonEmptyString),
  activeOrderKey: Schema.NullOr(TrimmedNonEmptyString),
});
export type ThreadPlacement = typeof ThreadPlacement.Type;

/**
 * An in-flight thread title regeneration. Its presence is the pending state a
 * client renders; `requestId` identifies the request that owns it, so a later
 * request supersedes an earlier one and a stale completion cannot close it.
 */
export const ThreadTitleRegeneration = Schema.Struct({
  requestId: CommandId,
  startedAt: IsoDateTime,
});
export type ThreadTitleRegeneration = typeof ThreadTitleRegeneration.Type;

/**
 * Where a thread's current title came from and whether it is settled.
 *
 * `source` is what keeps generation away from a name the user chose. `version`
 * identifies the act that set the title, so a generation that finishes after a
 * rename or a second request can be told apart from the one the thread still
 * wants; it changes even when a rename produces the same text. `needsRefinement`
 * is the model reporting that it could not tell what the thread is about yet,
 * which the Environment Service answers with one retitle once the turn has run.
 */
export const ThreadTitleState = Schema.Struct({
  source: Schema.Literals(["manual", "generated"]),
  version: CommandId,
  needsRefinement: Schema.Boolean,
});
export type ThreadTitleState = typeof ThreadTitleState.Type;

/** Every Design Operation. */
export const DesignActionId = Schema.Literals([
  "create-design",
  "update-design",
  "add-design-template",
  "add-design-example",
  "create-design-sample",
  "rename-design-system",
  "delete-design-system",
]);
export type DesignActionId = typeof DesignActionId.Type;

export const OrchestrationThread = Schema.Struct({
  providerGoal: Schema.optional(Schema.NullOr(ProviderGoalSnapshot)),
  id: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  legacyWorkingDirectory: LegacyThreadWorkingDirectory,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  worktreeBootstrap: Schema.optional(Schema.NullOr(ThreadWorktreeBootstrapIdentity)),
  ...ThreadPullRequestCollectionFields,
  latestTurn: Schema.NullOr(OrchestrationLatestTurn),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  management: Schema.optional(Schema.NullOr(ThreadManagement)),
  placement: Schema.optional(Schema.NullOr(ThreadPlacement)),
  /**
   * When the thread last re-entered the Active list, either by an explicit
   * restore or by activity outpacing its settle stamp. The Active list
   * anchors on it so a woken thread surfaces at the top instead of sinking
   * back into its creation-order slot.
   */
  unsettledAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  titleRegeneration: Schema.optional(Schema.NullOr(ThreadTitleRegeneration)),
  titleState: Schema.optional(Schema.NullOr(ThreadTitleState)),
  archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  deletedAt: Schema.NullOr(IsoDateTime),
  messages: Schema.Array(OrchestrationMessage),
  proposedPlans: Schema.Array(OrchestrationProposedPlan).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  activities: Schema.Array(OrchestrationThreadActivity),
  checkpoints: Schema.Array(OrchestrationCheckpointSummary),
  session: Schema.NullOr(OrchestrationSession),
});
export type OrchestrationThread = typeof OrchestrationThread.Type;

export const OrchestrationReadModel = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(OrchestrationProject),
  threads: Schema.Array(OrchestrationThread),
  updatedAt: IsoDateTime,
});
export type OrchestrationReadModel = typeof OrchestrationReadModel.Type;

export const OrchestrationProjectShell = Schema.Struct({
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.NullOr(ModelSelection),
  scripts: Schema.Array(ProjectScript),
  createdFrom: Schema.optional(ProjectCreationOrigin),
  study: Schema.optional(ProjectStudyRecord),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationProjectShell = typeof OrchestrationProjectShell.Type;

export const OrchestrationThreadShell = Schema.Struct({
  id: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  legacyWorkingDirectory: LegacyThreadWorkingDirectory,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  worktreeBootstrap: Schema.optional(Schema.NullOr(ThreadWorktreeBootstrapIdentity)),
  ...ThreadPullRequestCollectionFields,
  latestTurn: Schema.NullOr(OrchestrationLatestTurn),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  management: Schema.optional(Schema.NullOr(ThreadManagement)),
  placement: Schema.optional(Schema.NullOr(ThreadPlacement)),
  /**
   * When the thread last re-entered the Active list, either by an explicit
   * restore or by activity outpacing its settle stamp. The Active list
   * anchors on it so a woken thread surfaces at the top instead of sinking
   * back into its creation-order slot.
   */
  unsettledAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  titleRegeneration: Schema.optional(Schema.NullOr(ThreadTitleRegeneration)),
  titleState: Schema.optional(Schema.NullOr(ThreadTitleState)),
  archivedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  session: Schema.NullOr(OrchestrationSession),
  latestUserMessageAt: Schema.NullOr(IsoDateTime),
  hasPendingApprovals: Schema.Boolean,
  hasPendingUserInput: Schema.Boolean,
  hasActionableProposedPlan: Schema.Boolean,
});
export type OrchestrationThreadShell = typeof OrchestrationThreadShell.Type;

export const OrchestrationShellSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(OrchestrationProjectShell),
  threads: Schema.Array(OrchestrationThreadShell),
  updatedAt: IsoDateTime,
});
export type OrchestrationShellSnapshot = typeof OrchestrationShellSnapshot.Type;

export const OrchestrationShellStreamEvent = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("project-upserted"),
    sequence: NonNegativeInt,
    project: OrchestrationProjectShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("project-removed"),
    sequence: NonNegativeInt,
    projectId: ProjectId,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-upserted"),
    sequence: NonNegativeInt,
    thread: OrchestrationThreadShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-removed"),
    sequence: NonNegativeInt,
    threadId: ThreadId,
  }),
]);
export type OrchestrationShellStreamEvent = typeof OrchestrationShellStreamEvent.Type;

export const OrchestrationShellStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("snapshot"),
    snapshot: OrchestrationShellSnapshot,
  }),
  Schema.Struct({
    kind: Schema.Literal("ready"),
  }),
  OrchestrationShellStreamEvent,
]);
export type OrchestrationShellStreamItem = typeof OrchestrationShellStreamItem.Type;

export const OrchestrationShellBootstrapTicket = Schema.String.check(
  Schema.isPattern(/^esb_[A-Za-z0-9_-]{43}$/),
);
export type OrchestrationShellBootstrapTicket = typeof OrchestrationShellBootstrapTicket.Type;

export const OrchestrationShellBootstrapSnapshot = Schema.Struct({
  snapshot: OrchestrationShellSnapshot,
  throughSequence: NonNegativeInt,
});
export type OrchestrationShellBootstrapSnapshot = typeof OrchestrationShellBootstrapSnapshot.Type;

export const ORCHESTRATION_SHELL_BOOTSTRAP_PATH = "/api/orchestration/shell-bootstrap";

export const OrchestrationOptimizedShellStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("ticket"),
    ticket: OrchestrationShellBootstrapTicket,
    expiresAt: Schema.Int.check(Schema.isGreaterThan(0)),
  }),
  Schema.Struct({
    kind: Schema.Literal("ready"),
    throughSequence: NonNegativeInt,
  }),
  OrchestrationShellStreamEvent,
]);
export type OrchestrationOptimizedShellStreamItem =
  typeof OrchestrationOptimizedShellStreamItem.Type;

/** Opaque server-issued position for reading the next older user-turn window. */
export const OrchestrationThreadHistoryCursor = Schema.String.check(
  Schema.isPattern(/^thw1_[A-Za-z0-9_-]+$/),
  Schema.isMaxLength(512),
);
export type OrchestrationThreadHistoryCursor = typeof OrchestrationThreadHistoryCursor.Type;

export const OrchestrationThreadHistoryWindowRequest = Schema.Struct({
  maxDecodedBytes: PositiveInt,
  maxMessageCount: PositiveInt,
});
export type OrchestrationThreadHistoryWindowRequest =
  typeof OrchestrationThreadHistoryWindowRequest.Type;

export const OrchestrationSubscribeThreadInput = Schema.Struct({
  threadId: ThreadId,
  history: Schema.optionalKey(OrchestrationThreadHistoryWindowRequest),
});
export type OrchestrationSubscribeThreadInput = typeof OrchestrationSubscribeThreadInput.Type;

export const OrchestrationGetThreadHistoryPageInput = Schema.Struct({
  threadId: ThreadId,
  cursor: Schema.optionalKey(OrchestrationThreadHistoryCursor),
  ...OrchestrationThreadHistoryWindowRequest.fields,
});
export type OrchestrationGetThreadHistoryPageInput =
  typeof OrchestrationGetThreadHistoryPageInput.Type;

/** Revisions accepted by Environment resources with numeric or content-addressed authority. */
export const EnvironmentCommandExpectedRevision = Schema.Union([
  NonNegativeInt,
  TrimmedNonEmptyString,
]);
export type EnvironmentCommandExpectedRevision = typeof EnvironmentCommandExpectedRevision.Type;

export const OrchestrationThreadHistoryWindow = Schema.Struct({
  olderCursor: Schema.NullOr(OrchestrationThreadHistoryCursor),
  decodedBytes: NonNegativeInt,
  messageCount: NonNegativeInt,
  /**
   * The window's oldest message continues a turn whose earlier messages were left
   * behind the cursor. A turn too large to fit the requested budget is served this way
   * rather than refused, so the client can say the turn is partly loaded instead of
   * presenting a fragment as the start of the conversation.
   *
   * Optional because an Environment Service from an older release serves a window
   * without it, and compatibility here is whatever decodes. Absent reads as false.
   */
  startsMidTurn: Schema.optionalKey(Schema.Boolean),
});
export type OrchestrationThreadHistoryWindow = typeof OrchestrationThreadHistoryWindow.Type;

export const OrchestrationThreadDetailSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  thread: OrchestrationThread,
  history: Schema.optionalKey(OrchestrationThreadHistoryWindow),
});
export type OrchestrationThreadDetailSnapshot = typeof OrchestrationThreadDetailSnapshot.Type;

export const OrchestrationThreadHistoryPage = Schema.Struct({
  threadId: ThreadId,
  messages: Schema.Array(OrchestrationMessage),
  proposedPlans: Schema.Array(OrchestrationProposedPlan),
  activities: Schema.Array(OrchestrationThreadActivity),
  checkpoints: Schema.Array(OrchestrationCheckpointSummary),
  history: OrchestrationThreadHistoryWindow,
  /**
   * The projection sequence this page was read at, on the same numbering as a
   * thread subscription's snapshot and events. It tells a client whether the
   * page it just received describes the same history it is rendering:
   *
   * - behind the loaded state, and merging it could resurrect turns a newer
   *   snapshot or revert has already removed;
   * - ahead of it, and the page may carry content the subscription has not
   *   delivered yet, which replaying those events would then duplicate.
   *
   * Optional because an Environment Service from an older release serves a
   * page without it; absent means the client merges immediately, as before.
   */
  snapshotSequence: Schema.optionalKey(NonNegativeInt),
});
export type OrchestrationThreadHistoryPage = typeof OrchestrationThreadHistoryPage.Type;

export const ProjectCreateCommand = Schema.Struct({
  type: Schema.Literal("project.create"),
  commandId: CommandId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  createWorkspaceRootIfMissing: Schema.optional(Schema.Boolean),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  createdAt: IsoDateTime,
});

/**
 * One reference a new AFAR Project starts from. Images travel as plain files so
 * every reference reaches the agent as a path it reads, never as image input.
 */
export const UploadProjectReference = Schema.Union([
  UploadChatFileAttachment,
  UploadChatFolderAttachment,
]);
export type UploadProjectReference = typeof UploadProjectReference.Type;

/** A reference the Environment host can read in place, which is all creation dispatches. */
export const HostPathProjectReference = Schema.Union([
  UploadChatFileHostPathAttachment,
  UploadChatFolderHostPathAttachment,
]);
export type HostPathProjectReference = typeof HostPathProjectReference.Type;
export const isHostPathProjectReference = Schema.is(HostPathProjectReference);

/**
 * AFAR Project Creation: make the folder, place any Project References, and
 * open a Brain thread whose visible first message ingests every reference, as
 * one step that either fully happens or does not happen.
 */
export const ProjectCreateFromReferencesCommand = Schema.Struct({
  type: Schema.Literal("project.create-from-references"),
  commandId: CommandId,
  expectedRevision: NonNegativeInt,
  projectId: ProjectId,
  threadId: ThreadId,
  messageId: MessageId,
  destinationPath: TrimmedNonEmptyString,
  references: Schema.Array(UploadProjectReference).check(Schema.isMinLength(1)),
  modelSelection: ModelSelection,
  /** The ingest Skill Bundle bound natively; the Environment resolves it when absent. */
  skillInvocations: Schema.optional(ProviderSkillInvocationList),
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  createdAt: IsoDateTime,
});
export type ProjectCreateFromReferencesCommand = typeof ProjectCreateFromReferencesCommand.Type;

const ProjectMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("project.meta.update"),
  commandId: CommandId,
  projectId: ProjectId,
  expectedRevision: EnvironmentCommandExpectedRevision,
  title: Schema.optional(TrimmedNonEmptyString),
  workspaceRoot: Schema.optional(TrimmedNonEmptyString),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  scripts: Schema.optional(Schema.Array(ProjectScript)),
});

/**
 * Start a project's Project Study: record the start and open an Execution
 * thread whose visible first message is the user's statement of what to
 * study, in one sequence.
 */
export const ProjectStudyStartCommand = Schema.Struct({
  type: Schema.Literal("project.study.start"),
  commandId: CommandId,
  projectId: ProjectId,
  threadId: ThreadId,
  messageId: MessageId,
  study: TrimmedNonEmptyString.check(Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_INPUT_CHARS)),
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  createdAt: IsoDateTime,
});
export type ProjectStudyStartCommand = typeof ProjectStudyStartCommand.Type;

/** Keep just the Brain: record that the project's Project Study was skipped. */
export const ProjectStudySkipCommand = Schema.Struct({
  type: Schema.Literal("project.study.skip"),
  commandId: CommandId,
  projectId: ProjectId,
  createdAt: IsoDateTime,
});
export type ProjectStudySkipCommand = typeof ProjectStudySkipCommand.Type;

const ProjectDeleteCommand = Schema.Struct({
  type: Schema.Literal("project.delete"),
  commandId: CommandId,
  projectId: ProjectId,
  expectedRevision: EnvironmentCommandExpectedRevision,
  force: Schema.optional(Schema.Boolean),
});

const ThreadCreateCommand = Schema.Struct({
  type: Schema.Literal("thread.create"),
  commandId: CommandId,
  threadId: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  worktreeBootstrap: Schema.optional(ThreadWorktreeBootstrapIdentity),
  createdAt: IsoDateTime,
});

const ThreadForkCommand = Schema.Struct({
  type: Schema.Literal("thread.fork"),
  commandId: CommandId,
  sourceThreadId: ThreadId,
  targetThreadId: ThreadId,
  expectedRevision: IsoDateTime,
  assistantMessageId: MessageId,
  throughTurnId: TurnId,
  createdAt: IsoDateTime,
});

const ThreadDeleteCommand = Schema.Struct({
  type: Schema.Literal("thread.delete"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: EnvironmentCommandExpectedRevision,
});

export const ThreadManagementAction = Schema.Literals(["settle", "restore", "snooze", "unsnooze"]);
export type ThreadManagementAction = typeof ThreadManagementAction.Type;

const ThreadManagementCommand = Schema.Struct({
  type: Schema.Literal("thread.management.set"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: EnvironmentCommandExpectedRevision,
  action: ThreadManagementAction,
  until: Schema.optional(IsoDateTime),
});

const ThreadPinCommand = Schema.Struct({
  type: Schema.Literal("thread.pin"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: EnvironmentCommandExpectedRevision,
  /** Initial slot in the pinned order; omitted pins land below the arranged run. */
  orderKey: Schema.optional(TrimmedNonEmptyString),
});

const ThreadUnpinCommand = Schema.Struct({
  type: Schema.Literal("thread.unpin"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: EnvironmentCommandExpectedRevision,
});

const ThreadPinReorderCommand = Schema.Struct({
  type: Schema.Literal("thread.pin.reorder"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: EnvironmentCommandExpectedRevision,
  orderKey: TrimmedNonEmptyString,
});

const ThreadActiveReorderCommand = Schema.Struct({
  type: Schema.Literal("thread.active.reorder"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: EnvironmentCommandExpectedRevision,
  orderKey: TrimmedNonEmptyString,
});

/**
 * Thread pull request collection commands. `expectedRevision` is the collection's revision,
 * not the thread's: a refresh writing an observation must never make a user's link command
 * conflict. A client may only ever link as `manual`; `agent`, `created`, and `stack` are the
 * Environment's to assign, and `stack-dismissed` is reached only by unlinking.
 */
const ThreadPullRequestLinkCommandFields = {
  type: Schema.Literal("thread.pull-request.link"),
  commandId: CommandId,
  threadId: ThreadId,
  host: TrimmedNonEmptyString,
  repository: TrimmedNonEmptyString,
  number: PositiveInt,
  url: TrimmedNonEmptyString,
  expectedRevision: NonNegativeInt,
} as const;

const ThreadPullRequestLinkCommand = Schema.Struct({
  ...ThreadPullRequestLinkCommandFields,
  source: Schema.Literals(["manual", "agent", "created", "stack"]),
});

const ClientThreadPullRequestLinkCommand = Schema.Struct({
  ...ThreadPullRequestLinkCommandFields,
  source: Schema.Literal("manual"),
});

const ThreadPullRequestUnlinkCommand = Schema.Struct({
  type: Schema.Literal("thread.pull-request.unlink"),
  commandId: CommandId,
  threadId: ThreadId,
  host: TrimmedNonEmptyString,
  repository: TrimmedNonEmptyString,
  number: PositiveInt,
  expectedRevision: NonNegativeInt,
});

/** A read result. `null` is a successful empty answer; `"unavailable"` is a failed read. */
const ThreadPullRequestReadResult = <S extends Schema.Top>(value: S) =>
  Schema.Union([value, Schema.Null, Schema.Literal("unavailable")]);

/**
 * What a refresh found, guarded by the membership generation the read was issued against.
 * The Environment dispatches this; no client may write an observation.
 */
const ThreadPullRequestObserveCommand = Schema.Struct({
  type: Schema.Literal("thread.pull-request.observe"),
  commandId: CommandId,
  threadId: ThreadId,
  host: TrimmedNonEmptyString,
  repository: TrimmedNonEmptyString,
  number: PositiveInt,
  generation: PositiveInt,
  observation: ThreadPullRequestReadResult(ThreadPullRequestObservation),
  stack: ThreadPullRequestReadResult(ThreadPullRequestStack),
});

/** Native stack siblings a refresh found, guarded by the anchor membership's generation. */
const ThreadPullRequestDiscoverStackCommand = Schema.Struct({
  type: Schema.Literal("thread.pull-request.discover-stack"),
  commandId: CommandId,
  threadId: ThreadId,
  host: TrimmedNonEmptyString,
  repository: TrimmedNonEmptyString,
  number: PositiveInt,
  generation: PositiveInt,
  siblings: Schema.Array(
    Schema.Struct({
      host: TrimmedNonEmptyString,
      repository: TrimmedNonEmptyString,
      number: PositiveInt,
      url: TrimmedNonEmptyString,
    }),
  ),
});

const ThreadArchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.archive"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: EnvironmentCommandExpectedRevision,
});

const ThreadUnarchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.unarchive"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: EnvironmentCommandExpectedRevision,
});

const ThreadMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("thread.meta.update"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: EnvironmentCommandExpectedRevision,
  title: Schema.optional(TrimmedNonEmptyString),
  regenerateTitle: Schema.optional(Schema.Literal(true)),
  modelSelection: Schema.optional(ModelSelection),
  branch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
}).check(
  Schema.makeFilter(
    (input) =>
      !(input.title !== undefined && input.regenerateTitle === true) ||
      "title and regenerateTitle cannot be specified together",
  ),
);

/**
 * A first-turn title generation that finished. It names the title and version it
 * started from so the Environment Service can drop a result the thread has moved
 * past, rather than overwriting a rename that landed while the model was working.
 */
const ThreadTitleGenerateCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.title.generate.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedTitle: TrimmedNonEmptyString,
  expectedVersion: Schema.NullOr(CommandId),
  title: TrimmedNonEmptyString,
  needsRefinement: Schema.Boolean,
});

/** Retitle a thread whose first title admitted it did not know the subject yet. */
const ThreadTitleRefineCommand = Schema.Struct({
  type: Schema.Literal("thread.title.refine"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedVersion: CommandId,
});

const ThreadTitleRegenerationCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.title.regeneration.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: CommandId,
  title: Schema.optional(TrimmedNonEmptyString),
});

const ThreadRuntimeModeSetCommand = Schema.Struct({
  type: Schema.Literal("thread.runtime-mode.set"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: EnvironmentCommandExpectedRevision,
  runtimeMode: RuntimeMode,
  createdAt: IsoDateTime,
});

const ThreadInteractionModeSetCommand = Schema.Struct({
  type: Schema.Literal("thread.interaction-mode.set"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: EnvironmentCommandExpectedRevision,
  interactionMode: ProviderInteractionMode,
  createdAt: IsoDateTime,
});

const ThreadTurnStartBootstrapCreateThread = Schema.Struct({
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
});

const ThreadTurnStartBootstrapPrepareWorktree = Schema.Struct({
  projectCwd: TrimmedNonEmptyString,
  baseBranch: TrimmedNonEmptyString,
  branch: Schema.optional(TrimmedNonEmptyString),
  startFromOrigin: Schema.optional(Schema.Boolean),
  location: Schema.optional(ThreadWorktreeLocation),
});

const ThreadTurnStartBootstrap = Schema.Struct({
  createThread: Schema.optional(ThreadTurnStartBootstrapCreateThread),
  prepareWorktree: Schema.optional(ThreadTurnStartBootstrapPrepareWorktree),
  runSetupScript: Schema.optional(Schema.Boolean),
  expectedEnvironmentRevision: Schema.optional(NonNegativeInt),
});

export type ThreadTurnStartBootstrap = typeof ThreadTurnStartBootstrap.Type;

export const isDesignActionId = Schema.is(DesignActionId);

export const isDesignOperationSubmission = <T extends { readonly action: string }>(
  operation: T,
): operation is T & { readonly action: DesignActionId } => isDesignActionId(operation.action);

const StoredThreadTurnStartMessage = Schema.Struct({
  messageId: MessageId,
  role: Schema.Literal("user"),
  text: Schema.String,
  attachments: Schema.Array(ChatAttachment),
  designSystemMentions: DesignSystemMentionList,
  skillInvocations: Schema.optional(ProviderSkillInvocationList),
  action: Schema.optional(ThreadActionSelection),
});

const ClientThreadTurnStartMessage = Schema.Struct({
  messageId: MessageId,
  role: Schema.Literal("user"),
  text: Schema.String,
  attachments: Schema.Array(UploadChatAttachment),
  designSystemMentions: DesignSystemMentionList,
  skillInvocations: Schema.optional(ProviderSkillInvocationList),
  action: Schema.optional(ThreadActionSelection),
});

export const ThreadTurnStartCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.start"),
  commandId: CommandId,
  threadId: ThreadId,
  message: StoredThreadTurnStartMessage,
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  bootstrap: Schema.optional(ThreadTurnStartBootstrap),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  designOperation: Schema.optional(DesignOperationSubmission),
  createdAt: IsoDateTime,
});

const ClientThreadTurnStartCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.start"),
  commandId: CommandId,
  threadId: ThreadId,
  message: ClientThreadTurnStartMessage,
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  bootstrap: Schema.optional(ThreadTurnStartBootstrap),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  designOperation: Schema.optional(DesignOperationSubmission),
  createdAt: IsoDateTime,
});

/**
 * Rewind a Persistent Provider Thread to the end of `turnCount` turns,
 * restoring the Thread Scope workspace to that turn's checkpoint.
 *
 * The prompt that started the first discarded turn goes back to the composer;
 * nothing is re-executed. `turnCount` 0 rewinds to the pre-first-turn baseline.
 */
const ThreadCheckpointRevertCommand = Schema.Struct({
  type: Schema.Literal("thread.checkpoint.revert"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: IsoDateTime,
  turnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

/**
 * Rewind conversation history only, leaving the workspace exactly as it is.
 *
 * A separate command rather than an option on the one above so that an older
 * Environment Service rejects a history-only rewind outright instead of
 * ignoring an unfamiliar field and restoring files the user asked to keep.
 */
const ThreadConversationRevertCommand = Schema.Struct({
  ...ThreadCheckpointRevertCommand.fields,
  type: Schema.Literal("thread.conversation.revert"),
});

const ThreadGoalCommand = Schema.Struct({
  type: Schema.Literal("thread.goal"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: IsoDateTime,
  createdAt: IsoDateTime,
  ...ProviderGoalAction.fields,
  bootstrap: Schema.optional(ThreadTurnStartBootstrap),
});

const ThreadCompactCommand = Schema.Struct({
  type: Schema.Literal("thread.compact"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: IsoDateTime,
  createdAt: IsoDateTime,
});

const ThreadTurnInterruptCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.interrupt"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
  expectedRevision: IsoDateTime,
  createdAt: IsoDateTime,
});

const ThreadApprovalRespondCommand = Schema.Struct({
  type: Schema.Literal("thread.approval.respond"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  decision: ProviderApprovalDecision,
  expectedRevision: EnvironmentCommandExpectedRevision,
  turnId: Schema.NullOr(TurnId),
  createdAt: IsoDateTime,
});

const ThreadUserInputRespondCommand = Schema.Struct({
  type: Schema.Literal("thread.user-input.respond"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  answers: ProviderUserInputAnswers,
  expectedRevision: EnvironmentCommandExpectedRevision,
  turnId: Schema.NullOr(TurnId),
  createdAt: IsoDateTime,
});

const ThreadSessionStopCommand = Schema.Struct({
  type: Schema.Literal("thread.session.stop"),
  commandId: CommandId,
  threadId: ThreadId,
  expectedRevision: IsoDateTime,
  createdAt: IsoDateTime,
});

/**
 * Bring a thread's provider session up before it is needed.
 *
 * A Codex session spends most of its startup waiting for the Agent Provider's
 * MCP servers, and the provider will not begin a turn until they have all
 * reported ready. Asked for only when a send is plausible — the composer taking
 * focus — this moves that wait off the front of the first prompt. It carries no
 * expected revision because it mutates no thread state of its own and is
 * idempotent: an existing live session is reused.
 */
const ThreadSessionWarmCommand = Schema.Struct({
  type: Schema.Literal("thread.session.warm"),
  commandId: CommandId,
  threadId: ThreadId,
  createdAt: IsoDateTime,
});

const DispatchableClientOrchestrationCommand = Schema.Union([
  ProjectCreateCommand,
  ProjectCreateFromReferencesCommand,
  ProjectStudyStartCommand,
  ProjectStudySkipCommand,
  ProjectMetaUpdateCommand,
  ProjectDeleteCommand,
  ThreadCreateCommand,
  ThreadForkCommand,
  ThreadDeleteCommand,
  ThreadManagementCommand,
  ThreadPinCommand,
  ThreadUnpinCommand,
  ThreadPinReorderCommand,
  ThreadActiveReorderCommand,
  ThreadArchiveCommand,
  ThreadUnarchiveCommand,
  ClientThreadPullRequestLinkCommand,
  ThreadPullRequestUnlinkCommand,
  ThreadMetaUpdateCommand,
  ThreadRuntimeModeSetCommand,
  ThreadInteractionModeSetCommand,
  ThreadTurnStartCommand,
  ThreadGoalCommand,
  ThreadCompactCommand,
  ThreadTurnInterruptCommand,
  ThreadApprovalRespondCommand,
  ThreadUserInputRespondCommand,
  ThreadCheckpointRevertCommand,
  ThreadConversationRevertCommand,
  ThreadSessionStopCommand,
  ThreadSessionWarmCommand,
]);
export type DispatchableClientOrchestrationCommand =
  typeof DispatchableClientOrchestrationCommand.Type;

export const ClientOrchestrationCommand = Schema.Union([
  ProjectCreateCommand,
  ProjectCreateFromReferencesCommand,
  ProjectStudyStartCommand,
  ProjectStudySkipCommand,
  ProjectMetaUpdateCommand,
  ProjectDeleteCommand,
  ThreadCreateCommand,
  ThreadForkCommand,
  ThreadDeleteCommand,
  ThreadManagementCommand,
  ThreadPinCommand,
  ThreadUnpinCommand,
  ThreadPinReorderCommand,
  ThreadActiveReorderCommand,
  ThreadArchiveCommand,
  ThreadUnarchiveCommand,
  ClientThreadPullRequestLinkCommand,
  ThreadPullRequestUnlinkCommand,
  ThreadMetaUpdateCommand,
  ThreadRuntimeModeSetCommand,
  ThreadInteractionModeSetCommand,
  ClientThreadTurnStartCommand,
  ThreadGoalCommand,
  ThreadCompactCommand,
  ThreadTurnInterruptCommand,
  ThreadApprovalRespondCommand,
  ThreadUserInputRespondCommand,
  ThreadCheckpointRevertCommand,
  ThreadConversationRevertCommand,
  ThreadSessionStopCommand,
  ThreadSessionWarmCommand,
]);
export type ClientOrchestrationCommand = typeof ClientOrchestrationCommand.Type;

const ThreadTurnObserveCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.observe"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: TurnId,
  origin: TurnOrigin,
  createdAt: IsoDateTime,
});

const ThreadSessionSetCommand = Schema.Struct({
  type: Schema.Literal("thread.session.set"),
  commandId: CommandId,
  threadId: ThreadId,
  session: OrchestrationSession,
  turnOrigin: Schema.optional(TurnOrigin),
  turnStart: Schema.optional(
    Schema.Struct({
      messageId: MessageId,
      sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
    }),
  ),
  createdAt: IsoDateTime,
});

const ThreadMessageAssistantDeltaCommand = Schema.Struct({
  type: Schema.Literal("thread.message.assistant.delta"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  delta: Schema.String,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadMessageAssistantCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.message.assistant.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadProposedPlanUpsertCommand = Schema.Struct({
  type: Schema.Literal("thread.proposed-plan.upsert"),
  commandId: CommandId,
  threadId: ThreadId,
  proposedPlan: OrchestrationProposedPlan,
  createdAt: IsoDateTime,
});

const ThreadTurnDiffCompleteCommand = Schema.Struct({
  origin: Schema.optional(TurnOrigin),
  type: Schema.Literal("thread.turn.diff.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: TurnId,
  completedAt: IsoDateTime,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.optional(MessageId),
  checkpointTurnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

const ThreadActivityAppendCommand = Schema.Struct({
  type: Schema.Literal("thread.activity.append"),
  commandId: CommandId,
  threadId: ThreadId,
  activity: OrchestrationThreadActivity,
  createdAt: IsoDateTime,
});

/**
 * Recorded by the CheckpointReactor once a rewind has actually landed: the
 * workspace is restored, the provider conversation is rolled back, and stale
 * checkpoint refs are gone. Every projection truncates from the resulting
 * `thread.reverted`, never from the request.
 */
const ThreadRevertCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.revert.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

const ThreadForkCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.fork.complete"),
  commandId: CommandId,
  sourceThreadId: ThreadId,
  thread: OrchestrationThread,
  createdAt: IsoDateTime,
});

const ThreadGoalObserveCommand = Schema.Struct({
  type: Schema.Literal("thread.goal.observe"),
  commandId: CommandId,
  threadId: ThreadId,
  snapshot: ProviderGoalSnapshot,
  createdAt: IsoDateTime,
});

const InternalOrchestrationCommand = Schema.Union([
  ThreadPullRequestLinkCommand,
  ThreadPullRequestObserveCommand,
  ThreadPullRequestDiscoverStackCommand,
  ThreadGoalObserveCommand,
  ThreadTurnObserveCommand,
  ThreadSessionSetCommand,
  ThreadMessageAssistantDeltaCommand,
  ThreadMessageAssistantCompleteCommand,
  ThreadProposedPlanUpsertCommand,
  ThreadTurnDiffCompleteCommand,
  ThreadActivityAppendCommand,
  ThreadRevertCompleteCommand,
  ThreadForkCompleteCommand,
  ThreadTitleRegenerationCompleteCommand,
  ThreadTitleGenerateCompleteCommand,
  ThreadTitleRefineCommand,
]);
export type InternalOrchestrationCommand = typeof InternalOrchestrationCommand.Type;

export const OrchestrationCommand = Schema.Union([
  DispatchableClientOrchestrationCommand,
  InternalOrchestrationCommand,
]);
export type OrchestrationCommand = typeof OrchestrationCommand.Type;

export const OrchestrationEventType = Schema.Literals([
  "thread.turn-observed",
  "project.created",
  "project.meta-updated",
  "project.deleted",
  "thread.created",
  "thread.forked",
  "thread.deleted",
  "thread.management-updated",
  "thread.pinned",
  "thread.unpinned",
  "thread.pin-reordered",
  "thread.active-reordered",
  "thread.archived",
  "thread.unarchived",
  "thread.pull-request-linked",
  "thread.pull-request-restored",
  "thread.pull-request-dismissed",
  "thread.pull-request-observed",
  "thread.pull-request-unlinked",
  "thread.pull-request-stack-discovered",
  "thread.meta-updated",
  "thread.runtime-mode-set",
  "thread.interaction-mode-set",
  "thread.brain-composer-mode-set",
  "thread.design-composer-mode-set",
  "thread.message-sent",
  "thread.turn-start-requested",
  "thread.turn-interrupt-requested",
  "thread.approval-response-requested",
  "thread.user-input-response-requested",
  "thread.checkpoint-revert-requested",
  "thread.reverted",
  "thread.session-stop-requested",
  "thread.session-warm-requested",
  "thread.goal-observed",
  "thread.session-set",
  "thread.proposed-plan-upserted",
  "thread.turn-diff-completed",
  "thread.activity-appended",
]);
export type OrchestrationEventType = typeof OrchestrationEventType.Type;

export const OrchestrationAggregateKind = Schema.Literals(["project", "thread"]);
export type OrchestrationAggregateKind = typeof OrchestrationAggregateKind.Type;
export const OrchestrationActorKind = Schema.Literals(["client", "server", "provider"]);

export const ProjectCreatedPayload = Schema.Struct({
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.NullOr(ModelSelection),
  scripts: Schema.Array(ProjectScript),
  createdFrom: Schema.optional(ProjectCreationOrigin),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ProjectMetaUpdatedPayload = Schema.Struct({
  projectId: ProjectId,
  title: Schema.optional(TrimmedNonEmptyString),
  workspaceRoot: Schema.optional(TrimmedNonEmptyString),
  repositoryIdentity: Schema.optional(Schema.NullOr(RepositoryIdentity)),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  scripts: Schema.optional(Schema.Array(ProjectScript)),
  /** Set only by `project.study.start` / `project.study.skip`, once. */
  study: Schema.optional(ProjectStudyRecord),
  updatedAt: IsoDateTime,
});

export const ProjectDeletedPayload = Schema.Struct({
  projectId: ProjectId,
  deletedAt: IsoDateTime,
});

export const ThreadCreatedPayload = Schema.Struct({
  threadId: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  /**
   * The retired Thread Scope (ADR 0142), present only on threads created
   * before it was removed. Never written; read once to derive the thread's
   * legacy working directory.
   */
  scope: Schema.optionalKey(Schema.Unknown),
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  worktreeBootstrap: Schema.optional(ThreadWorktreeBootstrapIdentity),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadForkedPayload = Schema.Struct({
  sourceThreadId: ThreadId,
  thread: OrchestrationThread,
});

export const ThreadDeletedPayload = Schema.Struct({
  threadId: ThreadId,
  deletedAt: IsoDateTime,
});

export const ThreadManagementUpdatedPayload = Schema.Struct({
  threadId: ThreadId,
  management: ThreadManagement,
  updatedAt: IsoDateTime,
});

export const ThreadPinnedPayload = Schema.Struct({
  threadId: ThreadId,
  pinnedAt: IsoDateTime,
  /** Absent when re-pinning an already pinned thread: its existing key wins. */
  pinOrderKey: Schema.NullOr(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});

export const ThreadUnpinnedPayload = Schema.Struct({
  threadId: ThreadId,
  updatedAt: IsoDateTime,
});

export const ThreadPinReorderedPayload = Schema.Struct({
  threadId: ThreadId,
  pinOrderKey: TrimmedNonEmptyString,
  updatedAt: IsoDateTime,
});

export const ThreadActiveReorderedPayload = Schema.Struct({
  threadId: ThreadId,
  activeOrderKey: TrimmedNonEmptyString,
  updatedAt: IsoDateTime,
});

export const ThreadPullRequestLinkedPayload = Schema.Struct({
  threadId: ThreadId,
  link: ThreadPullRequestLink,
  revision: NonNegativeInt,
  updatedAt: IsoDateTime,
});

export const ThreadPullRequestUnlinkedPayload = Schema.Struct({
  threadId: ThreadId,
  host: TrimmedNonEmptyString,
  repository: TrimmedNonEmptyString,
  number: PositiveInt,
  revision: NonNegativeInt,
  updatedAt: IsoDateTime,
});

export const ThreadPullRequestStackDiscoveredPayload = Schema.Struct({
  threadId: ThreadId,
  added: Schema.Array(ThreadPullRequestLink),
  revision: NonNegativeInt,
  updatedAt: IsoDateTime,
});

export const ThreadArchivedPayload = Schema.Struct({
  threadId: ThreadId,
  archivedAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadUnarchivedPayload = Schema.Struct({
  threadId: ThreadId,
  updatedAt: IsoDateTime,
});

export const ThreadMetaUpdatedPayload = Schema.Struct({
  threadId: ThreadId,
  title: Schema.optional(TrimmedNonEmptyString),
  regenerateTitle: Schema.optional(Schema.Literal(true)),
  previousTitle: Schema.optional(TrimmedNonEmptyString),
  titleRegeneration: Schema.optional(Schema.NullOr(ThreadTitleRegeneration)),
  titleState: Schema.optional(Schema.NullOr(ThreadTitleState)),
  modelSelection: Schema.optional(ModelSelection),
  branch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  updatedAt: IsoDateTime,
});

export const ThreadRuntimeModeSetPayload = Schema.Struct({
  threadId: ThreadId,
  runtimeMode: RuntimeMode,
  updatedAt: IsoDateTime,
});

/**
 * Retired with Thread Scope (ADR 0142). Kept only so stored event logs still
 * decode; nothing writes these events and the projector ignores them.
 */
export const ThreadBrainComposerModeSetPayload = Schema.Struct({
  threadId: ThreadId,
  brainComposerMode: Schema.String,
  updatedAt: IsoDateTime,
});

/** Retired with Thread Scope (ADR 0142); see `ThreadBrainComposerModeSetPayload`. */
export const ThreadDesignComposerModeSetPayload = Schema.Struct({
  threadId: ThreadId,
  designComposerMode: Schema.String,
  updatedAt: IsoDateTime,
});

export const ThreadInteractionModeSetPayload = Schema.Struct({
  threadId: ThreadId,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  updatedAt: IsoDateTime,
});

export const ThreadMessageSentPayload = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  role: OrchestrationMessageRole,
  text: Schema.String,
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  designSystemMentions: DesignSystemMentionList,
  /** The Workspace Action this message submitted, queued or dispatched directly. */
  operation: Schema.optional(MessageOperationSubmission),
  /** The Action this message was sent with, when the user picked one. */
  action: Schema.optional(ThreadActionSelection),
  turnId: Schema.NullOr(TurnId),
  streaming: Schema.Boolean,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadTurnStartRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  skillInvocations: Schema.optional(ProviderSkillInvocationList),
  modelSelection: Schema.optional(ModelSelection),
  titleSeed: Schema.optional(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_RUNTIME_MODE))),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_PROVIDER_INTERACTION_MODE)),
  ),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  designOperation: Schema.optional(DesignOperationSubmission),
  createdAt: IsoDateTime,
});

export const ThreadTurnInterruptRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

export const ThreadApprovalResponseRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  decision: ProviderApprovalDecision,
  turnId: Schema.NullOr(TurnId),
  createdAt: IsoDateTime,
});

const ThreadUserInputResponseRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  answers: ProviderUserInputAnswers,
  turnId: Schema.NullOr(TurnId),
  createdAt: IsoDateTime,
});

export const ThreadCheckpointRevertRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  /** Absent means restore the workspace; `false` rewinds history only. */
  restoreFiles: Schema.optional(Schema.Boolean),
  createdAt: IsoDateTime,
});

export const ThreadRevertedPayload = Schema.Struct({
  threadId: ThreadId,
  turnCount: NonNegativeInt,
});

export const ThreadSessionStopRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  createdAt: IsoDateTime,
});

export const ThreadSessionWarmRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  createdAt: IsoDateTime,
});

export const ThreadSessionSetPayload = Schema.Struct({
  threadId: ThreadId,
  session: OrchestrationSession,
  turnOrigin: Schema.optional(TurnOrigin),
  turnStart: Schema.optional(
    Schema.Struct({
      messageId: MessageId,
      sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
    }),
  ),
});

export const ThreadProposedPlanUpsertedPayload = Schema.Struct({
  threadId: ThreadId,
  proposedPlan: OrchestrationProposedPlan,
});

export const ThreadTurnDiffCompletedPayload = Schema.Struct({
  origin: Schema.optional(TurnOrigin),
  threadId: ThreadId,
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});

export const ThreadActivityAppendedPayload = Schema.Struct({
  threadId: ThreadId,
  activity: OrchestrationThreadActivity,
});

export const OrchestrationEventMetadata = Schema.Struct({
  providerTurnId: Schema.optional(TrimmedNonEmptyString),
  providerItemId: Schema.optional(ProviderItemId),
  adapterKey: Schema.optional(TrimmedNonEmptyString),
  requestId: Schema.optional(ApprovalRequestId),
  ingestedAt: Schema.optional(IsoDateTime),
});
export type OrchestrationEventMetadata = typeof OrchestrationEventMetadata.Type;

const EventBaseFields = {
  sequence: NonNegativeInt,
  eventId: EventId,
  aggregateKind: OrchestrationAggregateKind,
  aggregateId: Schema.Union([ProjectId, ThreadId]),
  occurredAt: IsoDateTime,
  commandId: Schema.NullOr(CommandId),
  causationEventId: Schema.NullOr(EventId),
  correlationId: Schema.NullOr(CommandId),
  metadata: OrchestrationEventMetadata,
} as const;

export const OrchestrationEvent = Schema.Union([
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.created"),
    payload: ProjectCreatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.meta-updated"),
    payload: ProjectMetaUpdatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.deleted"),
    payload: ProjectDeletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.created"),
    payload: ThreadCreatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.forked"),
    payload: ThreadForkedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.deleted"),
    payload: ThreadDeletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.management-updated"),
    payload: ThreadManagementUpdatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.pinned"),
    payload: ThreadPinnedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.unpinned"),
    payload: ThreadUnpinnedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.pin-reordered"),
    payload: ThreadPinReorderedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.active-reordered"),
    payload: ThreadActiveReorderedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.pull-request-linked"),
    payload: ThreadPullRequestLinkedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.pull-request-restored"),
    payload: ThreadPullRequestLinkedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.pull-request-dismissed"),
    payload: ThreadPullRequestLinkedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.pull-request-observed"),
    payload: ThreadPullRequestLinkedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.pull-request-unlinked"),
    payload: ThreadPullRequestUnlinkedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.pull-request-stack-discovered"),
    payload: ThreadPullRequestStackDiscoveredPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.archived"),
    payload: ThreadArchivedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.unarchived"),
    payload: ThreadUnarchivedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.meta-updated"),
    payload: ThreadMetaUpdatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.runtime-mode-set"),
    payload: ThreadRuntimeModeSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.interaction-mode-set"),
    payload: ThreadInteractionModeSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.brain-composer-mode-set"),
    payload: ThreadBrainComposerModeSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.design-composer-mode-set"),
    payload: ThreadDesignComposerModeSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.message-sent"),
    payload: ThreadMessageSentPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-start-requested"),
    payload: ThreadTurnStartRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-interrupt-requested"),
    payload: ThreadTurnInterruptRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.approval-response-requested"),
    payload: ThreadApprovalResponseRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.user-input-response-requested"),
    payload: ThreadUserInputResponseRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.checkpoint-revert-requested"),
    payload: ThreadCheckpointRevertRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.reverted"),
    payload: ThreadRevertedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.session-stop-requested"),
    payload: ThreadSessionStopRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.session-warm-requested"),
    payload: ThreadSessionWarmRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-observed"),
    payload: Schema.Struct({
      threadId: ThreadId,
      turnId: TurnId,
      origin: TurnOrigin,
      createdAt: IsoDateTime,
    }),
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.goal-observed"),
    payload: Schema.Struct({ threadId: ThreadId, snapshot: ProviderGoalSnapshot }),
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.session-set"),
    payload: ThreadSessionSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.proposed-plan-upserted"),
    payload: ThreadProposedPlanUpsertedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-diff-completed"),
    payload: ThreadTurnDiffCompletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.activity-appended"),
    payload: ThreadActivityAppendedPayload,
  }),
]);
export type OrchestrationEvent = typeof OrchestrationEvent.Type;

export const OrchestrationThreadStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("not-found"),
  }),
  Schema.Struct({
    kind: Schema.Literal("snapshot"),
    snapshot: OrchestrationThreadDetailSnapshot,
  }),
  Schema.Struct({
    kind: Schema.Literal("ready"),
  }),
  Schema.Struct({
    kind: Schema.Literal("event"),
    continuitySequence: NonNegativeInt,
    event: OrchestrationEvent,
  }),
]);
export type OrchestrationThreadStreamItem = typeof OrchestrationThreadStreamItem.Type;

export const OrchestrationCommandReceiptStatus = Schema.Literals(["accepted", "rejected"]);
export type OrchestrationCommandReceiptStatus = typeof OrchestrationCommandReceiptStatus.Type;

export const TurnCountRange = Schema.Struct({
  fromTurnCount: NonNegativeInt,
  toTurnCount: NonNegativeInt,
}).check(
  Schema.makeFilter(
    (input) =>
      input.fromTurnCount <= input.toTurnCount ||
      new SchemaIssue.InvalidValue(Option.some(input.fromTurnCount), {
        message: "fromTurnCount must be less than or equal to toTurnCount",
      }),
    { identifier: "OrchestrationTurnDiffRange" },
  ),
);

export const ThreadTurnDiff = TurnCountRange.mapFields(
  Struct.assign({
    threadId: ThreadId,
    diff: Schema.String,
  }),
  { unsafePreserveChecks: true },
);

export const ProviderSessionRuntimeStatus = Schema.Literals([
  "starting",
  "running",
  "stopped",
  "error",
]);
export type ProviderSessionRuntimeStatus = typeof ProviderSessionRuntimeStatus.Type;

const ProjectionThreadTurnStatus = Schema.Literals([
  "running",
  "completed",
  "interrupted",
  "error",
]);
export type ProjectionThreadTurnStatus = typeof ProjectionThreadTurnStatus.Type;

const ProjectionCheckpointRow = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});
export type ProjectionCheckpointRow = typeof ProjectionCheckpointRow.Type;

export const ProjectionPendingApprovalStatus = Schema.Literals(["pending", "resolved", "expired"]);
export type ProjectionPendingApprovalStatus = typeof ProjectionPendingApprovalStatus.Type;

export const ProjectionPendingApprovalDecision = Schema.NullOr(ProviderApprovalDecision);
export type ProjectionPendingApprovalDecision = typeof ProjectionPendingApprovalDecision.Type;

/** Stable caller-created identity for one logical Environment mutation. */
export const EnvironmentCommandIdentity = Schema.Struct({
  commandId: CommandId,
});
export type EnvironmentCommandIdentity = typeof EnvironmentCommandIdentity.Type;

/** SHA-256 of the canonical, normalized Environment Command input. */
export const EnvironmentCommandNormalizedInputDigest = Schema.String.check(
  Schema.isPattern(/^[0-9a-f]{64}$/),
);
export type EnvironmentCommandNormalizedInputDigest =
  typeof EnvironmentCommandNormalizedInputDigest.Type;

export const EnvironmentCommandAcceptedWorkIdentity = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("direct-turn"),
    messageId: MessageId,
    turnId: Schema.NullOr(TurnId),
  }),
]);
export type EnvironmentCommandAcceptedWorkIdentity =
  typeof EnvironmentCommandAcceptedWorkIdentity.Type;

const EnvironmentCommandReceiptBase = {
  commandId: CommandId,
  commandKind: TrimmedNonEmptyString,
  normalizedInputDigest: EnvironmentCommandNormalizedInputDigest,
  completedAt: IsoDateTime,
} as const;

export const EnvironmentCommandAcceptedReceipt = Schema.Struct({
  ...EnvironmentCommandReceiptBase,
  status: Schema.Literal("accepted"),
  sequence: NonNegativeInt,
  result: Schema.Struct({
    sequence: NonNegativeInt,
    currentRevision: EnvironmentCommandExpectedRevision,
    acceptedWork: Schema.optional(EnvironmentCommandAcceptedWorkIdentity),
  }),
}).check(
  Schema.makeFilter(
    (receipt) =>
      receipt.sequence === receipt.result.sequence ||
      "Accepted receipt top-level and result sequences must describe one committed authority.",
    { identifier: "EnvironmentCommandAcceptedReceiptAuthority" },
  ),
);
export type EnvironmentCommandAcceptedReceipt = typeof EnvironmentCommandAcceptedReceipt.Type;

export const EnvironmentCommandRejectedCode = Schema.Literals([
  "command-rejected",
  "revision-conflict",
  "authority-unavailable",
  "resource-not-found",
  /** A new thread in a project that awaits its Project Study. */
  "project-awaits-study",
]);
export type EnvironmentCommandRejectedCode = typeof EnvironmentCommandRejectedCode.Type;

export const EnvironmentCommandRejectedReceipt = Schema.Struct({
  ...EnvironmentCommandReceiptBase,
  status: Schema.Literal("rejected"),
  result: Schema.Struct({
    code: EnvironmentCommandRejectedCode,
    message: TrimmedNonEmptyString,
    currentRevision: Schema.NullOr(EnvironmentCommandExpectedRevision),
  }),
});
export type EnvironmentCommandRejectedReceipt = typeof EnvironmentCommandRejectedReceipt.Type;

export const EnvironmentCommandReceipt = Schema.Union([
  EnvironmentCommandAcceptedReceipt,
  EnvironmentCommandRejectedReceipt,
]);
export type EnvironmentCommandReceipt = typeof EnvironmentCommandReceipt.Type;

/** The authenticated protocol returns the durable accepted receipt itself. */
export const DispatchResult = EnvironmentCommandAcceptedReceipt;
export type DispatchResult = typeof DispatchResult.Type;

export const OrchestrationGetTurnDiffInput = TurnCountRange.mapFields(
  Struct.assign({
    threadId: ThreadId,
    ignoreWhitespace: Schema.optionalKey(Schema.Boolean),
  }),
  { unsafePreserveChecks: true },
);
export type OrchestrationGetTurnDiffInput = typeof OrchestrationGetTurnDiffInput.Type;

export const OrchestrationGetTurnDiffResult = ThreadTurnDiff;
export type OrchestrationGetTurnDiffResult = typeof OrchestrationGetTurnDiffResult.Type;

export const OrchestrationGetFullThreadDiffInput = Schema.Struct({
  threadId: ThreadId,
  toTurnCount: NonNegativeInt,
  ignoreWhitespace: Schema.optionalKey(Schema.Boolean),
});
export type OrchestrationGetFullThreadDiffInput = typeof OrchestrationGetFullThreadDiffInput.Type;

export const OrchestrationGetFullThreadDiffResult = ThreadTurnDiff;
export type OrchestrationGetFullThreadDiffResult = typeof OrchestrationGetFullThreadDiffResult.Type;

export const OrchestrationReplayEventsInput = Schema.Struct({
  fromSequenceExclusive: NonNegativeInt,
});
export type OrchestrationReplayEventsInput = typeof OrchestrationReplayEventsInput.Type;

const OrchestrationReplayEventsResult = Schema.Array(OrchestrationEvent);
export type OrchestrationReplayEventsResult = typeof OrchestrationReplayEventsResult.Type;

export const OrchestrationRpcSchemas = {
  dispatchCommand: {
    input: ClientOrchestrationCommand,
    output: DispatchResult,
  },
  getTurnDiff: {
    input: OrchestrationGetTurnDiffInput,
    output: OrchestrationGetTurnDiffResult,
  },
  getFullThreadDiff: {
    input: OrchestrationGetFullThreadDiffInput,
    output: OrchestrationGetFullThreadDiffResult,
  },
  replayEvents: {
    input: OrchestrationReplayEventsInput,
    output: OrchestrationReplayEventsResult,
  },
  getArchivedShellSnapshot: {
    input: Schema.Struct({}),
    output: OrchestrationShellSnapshot,
  },
  getThreadHistoryPage: {
    input: OrchestrationGetThreadHistoryPageInput,
    output: OrchestrationThreadHistoryPage,
  },
  synchronizeHostViewOptimized: {
    input: Schema.Struct({}),
    output: OrchestrationOptimizedShellStreamItem,
  },
  subscribeThread: {
    input: OrchestrationSubscribeThreadInput,
    output: OrchestrationThreadStreamItem,
  },
  synchronizeHostView: {
    input: Schema.Struct({}),
    output: OrchestrationShellStreamItem,
  },
} as const;

export class OrchestrationGetSnapshotError extends Schema.TaggedErrorClass<OrchestrationGetSnapshotError>()(
  "OrchestrationGetSnapshotError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export class OrchestrationThreadHistoryError extends Schema.TaggedErrorClass<OrchestrationThreadHistoryError>()(
  "OrchestrationThreadHistoryError",
  {
    reason: Schema.Literals([
      "invalid-cursor",
      "thread-not-found",
      /**
       * No Environment refuses a window for its size any more; a thread too large for
       * the requested budget is served degraded instead. The reason stays decodable so
       * an older Environment's refusal still arrives as this typed error.
       */
      "window-too-large",
      "load-failed",
    ]),
    message: TrimmedNonEmptyString,
  },
) {}

export class OrchestrationShellBootstrapError extends Schema.TaggedErrorClass<OrchestrationShellBootstrapError>()(
  "OrchestrationShellBootstrapError",
  {
    reason: Schema.Literals([
      "ticket-unavailable",
      "snapshot-failed",
      "replay-failed",
      "replay-bound-exceeded",
      "catch-up-unproven",
    ]),
    message: TrimmedNonEmptyString,
  },
) {}

export class OrchestrationDispatchCommandError extends Schema.TaggedErrorClass<OrchestrationDispatchCommandError>()(
  "OrchestrationDispatchCommandError",
  {
    message: TrimmedNonEmptyString,
    rejectionCode: Schema.optional(EnvironmentCommandRejectedCode),
    currentRevision: Schema.optional(EnvironmentCommandExpectedRevision),
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export class EnvironmentCommandIdempotencyConflictError extends Schema.TaggedErrorClass<EnvironmentCommandIdempotencyConflictError>()(
  "EnvironmentCommandIdempotencyConflictError",
  {
    code: Schema.Literal("idempotency-conflict"),
    commandId: CommandId,
    existingCommandKind: TrimmedNonEmptyString,
    attemptedCommandKind: TrimmedNonEmptyString,
  },
) {}

export class EnvironmentCommandRejectedError extends Schema.TaggedErrorClass<EnvironmentCommandRejectedError>()(
  "EnvironmentCommandRejectedError",
  {
    receipt: EnvironmentCommandRejectedReceipt,
  },
) {}

export class OrchestrationGetTurnDiffError extends Schema.TaggedErrorClass<OrchestrationGetTurnDiffError>()(
  "OrchestrationGetTurnDiffError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export class OrchestrationGetFullThreadDiffError extends Schema.TaggedErrorClass<OrchestrationGetFullThreadDiffError>()(
  "OrchestrationGetFullThreadDiffError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export class OrchestrationReplayEventsError extends Schema.TaggedErrorClass<OrchestrationReplayEventsError>()(
  "OrchestrationReplayEventsError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}
